import styled from 'styled-components'

export const OrganizationButton = styled.div`
    border-radius: 8px;
    background-color: ${({ theme }) => theme.brandColors.CA_Lightest_Gray};
    border: 1px solid ${({ theme }) => theme.brandColors.CA_Light_Gray};
    padding: 2rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    text-align: left;
    transition: ${({ theme }) => theme.transition};
    cursor: pointer;

    .subtitle {
        font-weight: 300;
        color: ${({ theme }) => theme.brandColors.CA_Dark_Gray};
        font-size: 1rem;
    }

    &:not([disabled]):hover {
        background-color: #eaeaea;
    }

    @media only screen and (max-width: ${({ theme }) => theme.breakPoints.lg}) {
        width: 100%;
    }
`

export const AddedDeviceButton = styled(OrganizationButton)`
    padding: 1.25rem 1.25rem 1.25rem 2.5rem;
    flex-direction: row;
    align-items: center;

    &[disabled] {
        cursor: default;
    }

    @media only screen and (max-width: ${({ theme }) => theme.breakPoints.lg}) {
        padding: 0.75rem 1.5rem;
    }
`

export const ProductNameSpan = styled.span`
    height: 20px;
    color: ${({ theme }) => theme.brandColors.CA_Dark_Gray};
    font-family: 'Lateral Extended';
    overflow: hidden;
    text-overflow: ellipsis;
`

export const ChargerTypeButton = styled.button`
    align-items: center;
    border-radius: 8px;
    background-color: ${({ theme }) => theme.brandColors.CA_Lightest_Gray};
    border: 1px solid ${({ theme }) => theme.brandColors.CA_Light_Gray};
    padding: 2rem;
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    gap: 1.5rem;

    @media only screen and (max-width: ${({ theme }) => theme.breakPoints.lg}) {
        flex-direction: row;
        justify-content: flex-start;
        padding: 1.5rem 2rem;

        svg,
        #info-icon {
            min-height: 60px;
            height: 60px;
            min-width: 60px;
            width: 60px;
        }
    }
`

export const SkipLink = styled.button`
    display: inline-block;
    padding: 0;
    background: none;
    border: 0;
    outline: 0;
    color: #337ab7;
    cursor: pointer;

    .underline {
        text-decoration: underline;
    }
`

export const LastSavedLabel = styled.span`
    display: flex;
    justify-content: flex-end;
    font-weight: 300;
    color: ${({ theme }) => theme.brandColors.CA_Dark_Gray};
`

export const ErrorMsg = styled.div`
    display: block;
    height: 15px;
    position: relative;
    top: 15px;
    margin-top: -15px;
    padding: 2px 0;
    color: ${({ theme }) => theme.font.errorColor};

    &.default-color {
        color: inherit;
    }

    &.comment {
        font-weight: 300;
    }
`

export const Label = styled.label`
    display: block;
    margin-bottom: 2px;
    font-weight: 300;
    color: ${({ theme }) => theme.font.darkestColor};

    &.inline {
        display: inline;
    }

    &.space {
        margin-bottom: 10px;
    }

    &.error {
        color: ${({ theme }) => theme.font.errorColor};
    }
`

export const InputContainer = styled.div`
    display: inline-block;
`

/*
----- Button ----- */
export const Button = styled.button`
    min-width: 150px;
    padding: 9px 30px;
    border: none;
    background-color: #000;
    color: #fff;
    font-weight: 400;
    letter-spacing: 0.01em;
    border-radius: 16px;
    font-size: 1.5rem;
    transition: 0.2s ease all;

    @media only screen and (max-width: ${({ theme }) => theme.breakPoints.lg}) {
        width: 50%;
    }

    &.margin-right {
        margin-right: 20px;

        @media only screen and (min-width: ${({ theme }) => theme.breakPoints.lg}) {
            margin-right: 40px;
        }
    }

    &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.brandColors.CA_Green};
        color: ${({ theme }) => theme.font.darkestColor};
    }

    &:active:not(:disabled) {
        background-color: ${({ theme }) => theme.brandColors.CA_Light_Green};
        color: ${({ theme }) => theme.font.darkestColor};
    }

    &:focus {
        color: #fff;
        border: none;
    }

    &[disabled] {
        color: #fff;
        opacity: 0.3;
    }

    &.inverted {
        background-color: ${({ theme }) => theme.whiteBackground};
        color: ${({ theme }) => theme.font.darkestColor};
        border: 1px solid #000;

        &:hover:not(:disabled) {
            color: ${({ theme }) => theme.font.darkestColor};
            background-color: ${({ theme }) => theme.font.midLightColor};
        }

        &[disabled] {
            opacity: 0.5;
        }

        &:active:not(:disabled) {
            background-color: ${({ theme }) => theme.font.lightestColor};
            border: 1px solid ${({ theme }) => theme.font.midLightColor};
            color: ${({ theme }) => theme.font.midLightColor};
        }
    }
`

/*
----- List ----- */
export const ListContainer = styled.div`
    max-height: 500px;
    overflow-y: auto;
`

export const ListRow = styled.div`
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 16px;
    border: 1px solid #b3b3b3;
    border-bottom-width: 0;

    &:last-of-type {
        border-bottom-width: 1px;
    }
`

export const ListColGroup = styled.div`
    display: flex;
    flex-flow: column nowrap;
    justify-content: space-between;
    gap: 10px;
    width: 50%;

    & > * {
        flex-basis: 0;
    }

    @media only screen and (min-width: ${({ theme }) => theme.breakPoints.lg}) {
        flex-direction: row;
        gap: 0 20px;
    }
`

const ColStyle = `
    display: flex;
    flex: 1;
    text-align: left;
    text-overflow: ellipsis;
    flex-basis: 0;
    align-items: center;
`

export const ListCol = styled.div`
    ${ColStyle}

    &.right {
        display: block;
        text-align: right;
    }

    button {
        padding: 3px 20px;
        background-color: #000;
        font-weight: 400;
        color: #fff;
    }
`

export const SActionBtnWrapper = styled.div`
    display: grid;
    grid-template-areas: 'secondary primary';
    grid-template-columns: 1fr 1fr;
    width: 100%;
`

export const ActionBtnGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 30px;

    &.primary {
        grid-area: primary;
        justify-content: flex-end;
    }

    &.secondary {
        grid-area: secondary;
        justify-content: flex-start;
    }
`

export const PhaseButtonGroup = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;

    > div:first-child {
        input {
            border-right: unset;
        }
    }

    > div:not(:first-child):not(:last-child) {
        input {
            border-left: unset;
            border-right: unset;
        }
    }

    > div:last-child {
        input {
            border-left: unset;
        }
    }
`

export const ComponentContainer = styled.div`
    border: 1px solid #dddddd;
    padding: 2rem;
    border-radius: 4px;
    margin-bottom: 2rem;
    display: flex;
    flex-flow: row;
    flex-wrap: nowrap;
    gap: 2rem;

    @media (max-width: 576px) {
        flex-wrap: wrap;
    }
`

export const GutteredInputGroup = styled.div`
    > div {
        max-width: calc(50% - 0.85rem);

        @media (max-width: 576px) {
            max-width: 100%;
        }
    }
`
